<template>
  <div class="min-h-full bg-transparent text-slate-900 dark:text-[#F1F5F9] pb-16">
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Top Header Card -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-4 md:p-5 shadow-xs transition-all duration-200 flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 shrink-0"
          >
            <Cpu class="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 class="text-base sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
              Smart Cards & Hardware Devices
            </h1>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Monitor RFID terminals, allocate smart ID cards, and track digital identity
            </p>
          </div>
        </div>

        <!-- Quick Status Metrics Bar -->
        <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
          <div class="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449] flex items-center gap-2 shadow-2xs">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span class="text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">Gateway: Online</span>
          </div>
          <div class="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449] flex items-center gap-2 shadow-2xs">
            <Cpu class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            <span class="text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">Terminals: {{ totalDevices }}</span>
          </div>
          <div class="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449] flex items-center gap-2 shadow-2xs">
            <CreditCard class="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            <span class="text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">Cards: {{ totalCards }}</span>
          </div>
        </div>
      </div>

      <!-- 2-Segment Tab Switcher -->
      <div class="flex justify-center">
        <div
          class="w-full max-w-md grid grid-cols-2 p-1 bg-slate-100/90 dark:bg-[#18243A] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-inner gap-1"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            type="button"
            :class="[
              'flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all duration-150 select-none cursor-pointer',
              activeTab === tab.id
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-[#263449]'
                : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-[#F1F5F9]',
            ]"
          >
            <component
              :is="tab.icon"
              :class="[
                'w-4 h-4 transition-colors shrink-0',
                activeTab === tab.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-[#7E8DA5]',
              ]"
            />
            <span class="truncate">{{ tab.label }}</span>
          </button>
        </div>
      </div>

      <!-- Tab Content Area -->
      <div class="relative">
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div :key="activeTab">
            <div v-if="activeTab === 'smartcards'">
              <SmartCardStatistics />
            </div>

            <div v-else-if="activeTab === 'devices'">
              <DeviceController />
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import { useStore } from "vuex";
  import { Smartphone, CreditCard, Cpu } from "lucide-vue-next";
  import DeviceController from "@/components/SmartCardAndDevice/Reader.vue";
  import SmartCardStatistics from "@/components/SmartCardAndDevice/Cards.vue";

  const store = useStore();
  const activeTab = ref("smartcards");

  const totalDevices = computed(() => (store.state.devices || []).length);
  const totalCards = computed(() => (store.state.smartCards || []).length);

  const tabs = [
    {
      id: "smartcards",
      label: "Smart Cards",
      description: "Manage, issue, and inspect digital ID allocations.",
      icon: CreditCard,
    },
    {
      id: "devices",
      label: "Manage Devices",
      description: "Control, pair, and monitor hardware RFID readers.",
      icon: Smartphone,
    },
  ];
</script>

<style scoped>
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.25s ease-in-out;
  }
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
</style>
